import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, User, Package, Sparkles, Heart, MapPin, LogOut, Settings, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { truncateString, cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useRecentProducts } from '@/hooks/useRecentProducts';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { ImageManager } from '@/lib/imageManager';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { toast } from 'sonner';

interface MobileTopNavigationProps {
  onSearchClick: () => void;
  onAuthClick: () => void;
  isCheckoutPage?: boolean;
}

const MobileTopNavigation = ({ onSearchClick, onAuthClick, isCheckoutPage = false }: MobileTopNavigationProps) => {
  const [isLogoVisible, setIsLogoVisible] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastOrder, setLastOrder] = useState<any>(null);
  const { user, profile, isAdmin, signOut } = useAuth();
  const { totalItems, openCart, navigateToCart, isCheckoutActive } = useCart();
  const { recentProducts } = useRecentProducts();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Logo visibility logic - hide when scrolling past 50px
      if (isCheckoutPage) {
        setIsLogoVisible(currentScrollY === 0);
      } else {
        setIsLogoVisible(currentScrollY < 50);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    if (isCheckoutPage) {
      handleScroll();
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isCheckoutPage]);

  // Fetch last order for logged in user
  useEffect(() => {
    const fetchLastOrder = async () => {
      if (!user) {
        setLastOrder(null);
        return;
      }

      try {
        const { data, error } = await (supabase as any)
          .from('orders')
          .select('id, order_number, status, created_at, total_amount')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (error) throw error;
        setLastOrder(data);
      } catch (error) {
        console.error('Error fetching last order:', error);
      }
    };

    if (isMenuOpen && user) {
      fetchLastOrder();
    }
  }, [isMenuOpen, user]);

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'ממתין',
      paid: 'שולם',
      processing: 'בטיפול',
      ready_for_pickup: 'מוכן',
      shipped: 'נשלח',
      completed: 'הושלם',
      cancelled: 'בוטל',
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      pending: 'bg-gray-100 text-gray-600',
      paid: 'bg-indigo-100 text-indigo-600',
      processing: 'bg-green-100 text-green-600',
      ready_for_pickup: 'bg-amber-100 text-amber-600',
      shipped: 'bg-blue-100 text-blue-600',
      completed: 'bg-emerald-100 text-emerald-600',
      cancelled: 'bg-red-100 text-red-600',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-600';
  };

  // Reordered: Home, Products, Cart, Search, Profile
  const mainNavItems = [
    {
      icon: Home,
      label: 'בית',
      path: '/',
      onClick: () => navigate('/')
    },
    {
      icon: Package,
      label: 'מוצרים',
      path: '/products',
      onClick: () => navigate('/products')
    },
    {
      icon: ShoppingCart,
      label: 'עגלה',
      path: '/cart',
      onClick: isCheckoutActive ? navigateToCart : openCart,
      badge: totalItems > 0 ? (totalItems > 99 ? '99+' : totalItems.toString()) : null
    },
    {
      icon: Search,
      label: 'חיפוש',
      onClick: onSearchClick
    },
    {
      icon: User,
      label: user && profile?.first_name ? truncateString(profile.first_name, 8) : 'אורח',
      onClick: (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
      },
      isDropdown: true
    }
  ];

  const menuDropdownItems = user ? [
    {
      icon: Package,
      label: 'הזמנות',
      path: '/profile?tab=orders',
      onClick: () => {
        navigate('/profile?tab=orders');
        setIsMenuOpen(false);
      }
    },
    {
      icon: User,
      label: 'פרטים אישיים',
      path: '/profile?tab=profile',
      onClick: () => {
        navigate('/profile?tab=profile');
        setIsMenuOpen(false);
      }
    },
    {
      icon: MapPin,
      label: 'כתובות',
      path: '/profile?tab=addresses',
      onClick: () => {
        navigate('/profile?tab=addresses');
        setIsMenuOpen(false);
      }
    },
    {
      icon: Heart,
      label: 'מועדפים',
      path: '/profile?tab=favorites',
      onClick: () => {
        navigate('/profile?tab=favorites');
        setIsMenuOpen(false);
      }
    },
    ...(isAdmin ? [{
      icon: Settings,
      label: 'לוח ניהול',
      path: '/admin',
      onClick: () => {
        navigate('/admin');
        setIsMenuOpen(false);
      }
    }] : []),
    {
      icon: LogOut,
      label: 'התנתק',
      path: null,
      onClick: async () => {
        await signOut();
        setIsMenuOpen(false);
        navigate('/');
      },
      isSeparator: true
    }
  ] : [
    {
      icon: User,
      label: 'התחברות',
      path: null,
      onClick: () => {
        onAuthClick();
        setIsMenuOpen(false);
      }
    },
    {
      icon: Heart,
      label: 'מועדפים',
      path: '/favorites',
      onClick: () => {
        navigate('/favorites');
        setIsMenuOpen(false);
      }
    }
  ];

  const isActive = (path: string | null) => {
    if (!path) return false;
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="lg:hidden">
      {/* Logo Strip - hides on scroll */}
      <div
        className={`bg-white border-b border-border transition-all duration-300 ease-in-out relative ${isLogoVisible
          ? 'opacity-100 translate-y-0 h-16'
          : 'opacity-0 -translate-y-full h-0 overflow-hidden'
          }`}
      >
        {/* Beta Ribbon */}
        <div className="absolute top-0 right-0 z-50 pointer-events-none overflow-hidden w-24 h-16">
          <span className="absolute top-3 -right-6 bg-red-500 text-white text-[10px] font-bold px-10 py-1 shadow-lg whitespace-nowrap transform rotate-45 origin-center text-center">
            בהרצה
          </span>
        </div>

        <div className="container mx-auto px-4 py-3">
          <Link to="/" className="relative flex items-center justify-center hover:opacity-80 transition-opacity">
            <img
              src="/uploads/ad1ab8aa-6b4f-439f-9472-027351fbb264.png"
              alt="Excess Market"
              className="h-10 w-auto"
              loading="eager"
              sizes="40px"
              srcSet="/uploads/ad1ab8aa-6b4f-439f-9472-027351fbb264.png 1009w"
            />
          </Link>
        </div>
      </div>

      {/* Main Navigation - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-border z-40 pb-safe">
        {/* Navigation Items */}
        <div className="grid grid-cols-5 gap-0.5 py-2 px-1">
          {mainNavItems.map((item: any, index) => {
            const active = isActive(item.path || null);
            return (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                disabled={item.disabled}
                onClick={item.onClick}
                className={`flex flex-col items-center gap-1 h-auto py-3 px-1 relative transition-all duration-200 hover:scale-105 hover:bg-transparent ${active ? 'text-primary' : ''
                  } ${item.isDropdown && isMenuOpen ? 'text-primary bg-muted' : ''} ${item.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <div className="relative">
                  <item.icon className="h-6 w-6" />
                  {item.badge && (
                    <Badge className={`absolute -top-3 -right-4 flex items-center justify-center p-0 bg-secondary text-secondary-foreground text-[9px] h-4 min-w-[30px] px-1 animate-scale-in whitespace-nowrap ${item.label === 'עגלה' ? 'bg-destructive text-destructive-foreground w-5 h-5 rounded-full min-w-0 right-[-10px] top-[-10px] text-[10px] shadow-sm' : ''}`}>
                      {item.badge}
                    </Badge>
                  )}
                </div>
                <span className={`text-xs font-medium whitespace-nowrap relative ${active ? 'text-primary' : ''
                  }`}>
                  {item.label}
                  {active && (
                    <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary rounded-full"></div>
                  )}
                </span>
              </Button>
            );
          })}
        </div>
      </div>

      {/* Profile Drawer */}
      <Drawer open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DrawerContent className="max-h-[85vh] outline-none" dir="rtl">
          <DrawerHeader className="text-right border-b pb-4 px-6">
            <DrawerTitle className="text-xl font-bold">
              {user ? `שלום, ${profile?.first_name || 'אורח'}` : 'שלום, אורח'}
            </DrawerTitle>
            <DrawerDescription>
              {user ? 'נהל את החשבון וההזמנות שלך' : 'התחבר כדי ליהנות מחוויה מותאמת אישית'}
            </DrawerDescription>
          </DrawerHeader>

          <div className="overflow-y-auto px-6 py-4 space-y-8 scrollbar-hide pb-10">
            {/* Last Order Status - Logged In Only */}
            {user && lastOrder && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    סטטוס הזמנה אחרונה
                  </h3>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-primary"
                    onClick={() => {
                      navigate('/profile?tab=orders');
                      setIsMenuOpen(false);
                    }}
                  >
                    כל ההזמנות
                  </Button>
                </div>
                <div className="bg-muted/40 p-4 rounded-xl border border-border flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">הזמנה #{lastOrder.order_number}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(lastOrder.created_at).toLocaleDateString('he-IL')}
                    </p>
                  </div>
                  <Badge className={cn("px-3 py-1", getStatusColor(lastOrder.status))}>
                    {getStatusLabel(lastOrder.status)}
                  </Badge>
                </div>
              </div>
            )}

            {/* Navigation Grid */}
            <div className="grid grid-cols-2 gap-3">
              {menuDropdownItems.map((item, index) => {
                const isSignOut = item.label === 'התנתק';
                return (
                  <Button
                    key={index}
                    variant="outline"
                    className={cn(
                      "h-auto py-4 flex flex-col gap-2 items-center justify-center border-border hover:border-primary hover:bg-primary/5 transition-all rounded-xl",
                      isSignOut && "col-span-2 border-destructive/20 text-destructive hover:bg-destructive/5 hover:border-destructive"
                    )}
                    onClick={item.onClick}
                  >
                    <item.icon className={cn("h-5 w-5", isSignOut ? "text-destructive" : "text-primary")} />
                    <span className="text-xs font-medium">{item.label}</span>
                  </Button>
                );
              })}
            </div>

            {/* Recently Viewed Products */}
            {recentProducts.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    נצפה לאחרונה
                  </h3>
                </div>
                <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide -mx-2 px-2">
                  {recentProducts.map((p) => (
                    <div
                      key={p.id}
                      className="min-w-[120px] max-w-[120px] group cursor-pointer"
                      onClick={() => {
                        navigate(`/product/${p.slug}`);
                        setIsMenuOpen(false);
                      }}
                    >
                      <div className="aspect-square bg-muted rounded-xl mb-2 overflow-hidden border border-border group-hover:border-primary transition-colors">
                        <img
                          src={ImageManager.getImageUrl(p.images[0])}
                          alt={p.name}
                          className="w-full h-full object-cover"
                          onError={(e) => ImageManager.handleImageError(e)}
                        />
                      </div>
                      <p className="text-[10px] font-medium line-clamp-2 mb-1">{p.name}</p>
                      <p className="text-[10px] font-bold text-primary">₪{p.price_per_unit}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
};

export default MobileTopNavigation;